import { useEffect, useRef } from 'react';

import {
  Box,
  Button,
  Flex,
  Text,
  Tooltip,
  useToast,
  Link,
  useDisclosure,
  VStack,
  Spinner,
} from '@chakra-ui/react';

import { useAppSelector, useAppDispatch } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { useGroupedBillingSync } from 'hooks/useGroupedBillingSync';
import { useGroupedBinder } from 'hooks/useGroupedBinder';

import { setSigning as setAutoSigning } from 'reducers/autoPersonalFormReducer';
import { setSigning as setHomeSigning } from 'reducers/homePersonalFormReducer';

import type { RootState } from 'store';

import {
  SIGNING_STATUS_PENDING,
  SIGNING_STATUS_COMPLETED,
  SIGNING_STATUS_ERROR,
  SIGNING_STATUS_CANCELLED,
  BINDER_STATUS_COMPLETED,
  STATUS_PENDING_CANCELLATION,
} from 'variables/constants';

import StepContainer from './StepContainer';
import StepHeader from './StepHeader';
import StatusBadge from './StatusBadge';
import SigningLogModal from './modals/SigningLogModal';
import WetSignUploadModal from './modals/WetSignUploadModal';
import CancelSigningModal from './modals/CancelSigningModal';
import ConfirmSigningModal from './modals/ConfirmSigningModal';
import type { FormStateKey } from './stepTypes';

interface GroupedStepTwoProps {
  formStateKey: FormStateKey;
}

const GroupedStepTwo = ({ formStateKey }: GroupedStepTwoProps) => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    isOpen: isConfirmModalOpen,
    onOpen: onConfirmModalOpen,
    onClose: onConfirmModalClose,
  } = useDisclosure();

  const {
    isOpen: isWetSignModalOpen,
    onOpen: onWetSignModalOpen,
    onClose: onWetSignModalClose,
  } = useDisclosure();

  const {
    isOpen: isCancelModalOpen,
    onOpen: onCancelModalOpen,
    onClose: onCancelModalClose,
  } = useDisclosure();

  const isSigningReminderEnabled = useFeatureFlag('enable-e-signing-reminder');

  const uuid = useAppSelector((state: RootState) => state[formStateKey].uuid);
  const { policyGroupId } = useGroupedBillingSync(uuid);

  const {
    binderPdf,
    signing,
    signers,
    missingEmails,
    createESigning,
    uploadWetSigned,
    resetSigning,
    cancelSigning,
    sendReminder,
    isSigningMutationPending,
    isUploadPending,
    isResetPending,
    isCancelPending,
    isReminderPending,
    isPolling,
  } = useGroupedBinder(uuid, policyGroupId);

  const prevSigningRef = useRef<{ id: number | null; status: number | null }>({
    id: null,
    status: null,
  });

  useEffect(() => {
    const currentId = signing?.id ?? null;
    const currentStatus = signing?.status ?? null;
    const prev = prevSigningRef.current;

    if (prev.id === currentId && prev.status === currentStatus) {
      return;
    }

    prevSigningRef.current = { id: currentId, status: currentStatus };

    if (formStateKey === 'autoPersonalForm') {
      dispatch(setAutoSigning(signing));
    } else if (formStateKey === 'homePersonalForm') {
      dispatch(setHomeSigning(signing));
    }
  }, [signing, formStateKey, dispatch]);

  const handleSendForSigning = () => {
    onConfirmModalClose();
    createESigning();
  };

  const handleCancelSigning = () => {
    onCancelModalClose();
    cancelSigning();
  };

  const handleUploadWetSigned = (file: File) => {
    uploadWetSigned(file);
    onWetSignModalClose();
    toast({
      title: 'WET signed document uploaded successfully',
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });
  };

  const isBinderComplete =
    binderPdf && binderPdf.status === BINDER_STATUS_COMPLETED;
  const isStepEnabled = isBinderComplete;
  const showSigningButton = !signing;
  const showResetButton =
    signing &&
    (signing.status === SIGNING_STATUS_ERROR ||
      signing.status === SIGNING_STATUS_CANCELLED);
  const hasMissingEmails = missingEmails.length > 0;

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      applicant: 'Applicant',
      co_applicant: 'Co-Applicant',
      account_holder: 'Account Holder',
    };
    return labels[role] || role;
  };

  const getStatusContent = () => {
    if (!isBinderComplete) {
      return (
        <StatusBadge status="disabled">
          No binder has been generated
        </StatusBadge>
      );
    }

    if (!signing) {
      return null;
    }

    if (signing.status === SIGNING_STATUS_PENDING) {
      return (
        <VStack spacing="3" w="100%">
          <StatusBadge status="info">
            <Flex align="center" justify="center" gap="10px">
              {isPolling ? (
                <>
                  <Spinner size="sm" color="blue.500" />
                  <Text>Checking status...</Text>
                </>
              ) : (
                <Text>Signing in progress...</Text>
              )}
            </Flex>
          </StatusBadge>
          {signing.logs?.some(
            (log) =>
              log.type === 'document_created' || log.type === 'document_sent',
          ) && (
            <>
              {isSigningReminderEnabled && (
                <Button
                  onClick={() => sendReminder()}
                  disabled={isReminderPending}
                  isLoading={isReminderPending}
                  variant="outline"
                  colorScheme="brand"
                  size="sm"
                  w="100%"
                >
                  Send reminder
                </Button>
              )}
              <Button
                onClick={onCancelModalOpen}
                disabled={isCancelPending}
                isLoading={isCancelPending}
                variant="outline"
                colorScheme="red"
                size="sm"
                w="100%"
              >
                Cancel signing
              </Button>
            </>
          )}
        </VStack>
      );
    }

    if (signing.status === STATUS_PENDING_CANCELLATION) {
      return (
        <StatusBadge status="warning">
          <Flex align="center" justify="center" gap="10px">
            {isPolling ? (
              <>
                <Spinner size="sm" color="orange.500" />
                <Text>Checking status...</Text>
              </>
            ) : (
              <Text>Canceling in progress...</Text>
            )}
          </Flex>
        </StatusBadge>
      );
    }

    if (signing.status === SIGNING_STATUS_ERROR) {
      return (
        <StatusBadge status="error">
          Encountered an issue, please verify the emails are correct and the
          policy effective date/time is in the future and try again
        </StatusBadge>
      );
    }

    if (signing.status === SIGNING_STATUS_CANCELLED) {
      return (
        <StatusBadge status="warning">Signing has been canceled</StatusBadge>
      );
    }

    if (signing.status === SIGNING_STATUS_COMPLETED) {
      return <StatusBadge status="success">Signed by all parties</StatusBadge>;
    }

    return null;
  };

  return (
    <StepContainer isEnabled={!!isStepEnabled}>
      <StepHeader
        stepNumber={2}
        title="Document Signing"
        isEnabled={!!isStepEnabled}
      />

      {binderPdf && signing && signing.logs && signing.logs.length > 0 && (
        <Box mb="15px">
          <Link
            onClick={onOpen}
            color="brand.500"
            textDecoration="underline"
            fontSize="sm"
            fontWeight="semibold"
            cursor="pointer"
          >
            View signing logs
          </Link>
        </Box>
      )}

      {getStatusContent()}

      {isStepEnabled && showSigningButton && (
        <VStack spacing="5" w="100%">
          <Tooltip
            label={
              hasMissingEmails
                ? `Missing email for: ${missingEmails
                    .map((m) => `${m.name} (${getRoleLabel(m.role)})`)
                    .join(', ')}`
                : ''
            }
            isDisabled={!hasMissingEmails}
            hasArrow
          >
            <Box w="100%">
              <Button
                onClick={onConfirmModalOpen}
                disabled={
                  isSigningMutationPending ||
                  signers.length === 0 ||
                  hasMissingEmails
                }
                isLoading={isSigningMutationPending}
                variant="brand"
                size="sm"
                w="100%"
              >
                Send for E-Signing
              </Button>
            </Box>
          </Tooltip>
          <Button
            onClick={onWetSignModalOpen}
            disabled={isUploadPending}
            isLoading={isUploadPending}
            variant="outline"
            colorScheme="brand"
            size="sm"
            w="100%"
          >
            Upload WET signed Binder
          </Button>
        </VStack>
      )}

      {isStepEnabled && showResetButton && (
        <Button
          onClick={resetSigning}
          disabled={isResetPending}
          isLoading={isResetPending}
          variant="outline"
          colorScheme="red"
          size="sm"
          w="100%"
        >
          Reset signing
        </Button>
      )}

      <SigningLogModal isOpen={isOpen} onClose={onClose} logs={signing?.logs} />

      <ConfirmSigningModal
        isOpen={isConfirmModalOpen}
        onClose={onConfirmModalClose}
        onConfirm={handleSendForSigning}
        signers={signers}
        isLoading={isSigningMutationPending}
      />

      <WetSignUploadModal
        isOpen={isWetSignModalOpen}
        onClose={onWetSignModalClose}
        onUpload={handleUploadWetSigned}
        isLoading={isUploadPending}
      />

      <CancelSigningModal
        isOpen={isCancelModalOpen}
        onClose={onCancelModalClose}
        onCancel={handleCancelSigning}
        isLoading={isCancelPending}
      />
    </StepContainer>
  );
};

export default GroupedStepTwo;
